<template>
  <div class="px-6 py-6 max-w-5xl mx-auto flex flex-col gap-6">

    <!-- Verify Email Banner -->
    <div v-if="emailVerificationState === false" class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/50 rounded-[10px] p-5">
      <div class="flex items-start gap-4">
        <div class="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center shrink-0">
          <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        </div>
        <div class="flex-1">
          <h3 class="text-base font-semibold text-amber-900 dark:text-amber-100 mb-1">Verify your email</h3>
          <p class="text-sm text-amber-700 dark:text-amber-300 mb-3">Please verify your email address to complete your profile.</p>
          <button
            type="button"
            :disabled="isVerifyingEmail"
            class="px-4 py-2 text-sm font-semibold rounded-[7px] bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 transition-colors"
            @click="verifyEmail"
          >
            {{ isVerifyingEmail ? 'Sending...' : 'Resend verification email' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Greeting -->
    <div class="flex flex-col gap-2">
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-slate-100">{{ greeting }}<span v-if="hasHydrated && firstName">, {{ firstName }}</span>!</h1>
      <p class="text-gray-500 dark:text-slate-400 text-sm">Here's a summary of your calls today.</p>
    </div>

    <!-- Metrics Row -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Answered Calls -->
      <div
        class="flex flex-col justify-between h-28 p-5 bg-primary/10 border border-primary/20 rounded-[10px] cursor-pointer hover:bg-primary/15 transition-colors"
        @click="goToCalls('answered')"
      >
        <div class="w-8 h-8 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center shadow-sm">
          <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
        </div>
        <div class="flex justify-between items-end mt-2">
          <span class="font-medium text-gray-800 dark:text-slate-200">Answered Calls</span>
          <span class="text-2xl font-bold text-gray-900 dark:text-slate-100">{{ answeredCount }}</span>
        </div>
      </div>

      <!-- Missed Calls -->
      <div
        class="flex flex-col justify-between h-28 p-5 bg-danger/10 border border-danger/20 rounded-[10px] cursor-pointer hover:bg-danger/15 transition-colors"
        @click="goToCalls('missed')"
      >
        <div class="w-8 h-8 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center shadow-sm">
          <svg class="w-4 h-4 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L9.684 3.948A1 1 0 008.736 3H5z" /></svg>
        </div>
        <div class="flex justify-between items-end mt-2">
          <span class="font-medium text-gray-800 dark:text-slate-200">Missed Calls</span>
          <span class="text-2xl font-bold text-gray-900 dark:text-slate-100">{{ missedCount }}</span>
        </div>
      </div>
    </div>

    <!-- Promo Banner (similar to mobile) -->
    <div class="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-[10px] p-6 flex flex-row items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors" @click="goToSupport">
      <div class="flex-1">
        <h3 class="font-bold text-gray-900 dark:text-slate-100 mb-1">Get your own business number</h3>
        <p class="text-sm text-gray-600 dark:text-slate-400">Register or port an existing number for your teams today.</p>
      </div>
      <div class="shrink-0 ml-4">
        <!-- Placeholder emoji as fallback -->
        <span class="text-4xl text-primary">💼</span>
      </div>
    </div>

    <!-- Recent Calls Header -->
    <div class="mt-4">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">Recent Calls</h2>
      <hr class="border-gray-200 dark:border-slate-700 mb-4">

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>

      <!-- Empty State -->
      <div v-else-if="recentCalls.length === 0" class="text-center py-10 bg-gray-50 dark:bg-slate-800 rounded-[10px] border border-gray-100 dark:border-slate-800">
        <div class="w-12 h-12 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm text-gray-400">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
        </div>
        <h3 class="text-base font-medium text-gray-900 dark:text-slate-100">No recent calls</h3>
        <p class="text-sm text-gray-500 dark:text-slate-400 mt-1">Your recent call history will appear here.</p>
      </div>

      <!-- Recent Calls List -->
      <div v-else class="flex flex-col divide-y divide-gray-100 dark:divide-slate-800 border border-gray-100 dark:border-slate-800 rounded-[10px] overflow-hidden bg-white dark:bg-slate-900">
        <div 
          v-for="call in recentCalls" 
          :key="call.id" 
          class="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
        >
          <div class="flex items-center gap-4">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center"
              :class="{ 'bg-danger/10 text-danger': isCallMissed(call), 'bg-primary/10 text-primary': isCallAnswered(call) || isCallOutbound(call), 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400': getCallOutcome(call.hangupCause) === 'unknown' && !isCallOutbound(call) }"
            >
              <template v-if="isCallMissed(call)">
                 <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L9.684 3.948A1 1 0 008.736 3H5z" /></svg>
              </template>
              <template v-else-if="isCallInbound(call)">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
              </template>
              <template v-else>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
              </template>
            </div>
            <div class="flex flex-col">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-slate-100">{{ getDisplayTitle(call) }}</h4>
              <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                <span v-if="getMatchedName(call)" class="font-medium">{{ getActualNumber(call) }}</span>
                <span v-if="getMatchedName(call)" class="text-gray-300">&bull;</span>
                <span>{{ formatTimeInfo(call) }}</span>
              </div>
            </div>
          </div>
          <div class="text-right flex flex-col items-end">
            <span class="text-xs font-medium text-gray-500 dark:text-slate-400">{{ formatDuration(call.durationSeconds) }}</span>
            <span v-if="call.did" class="text-xs text-gray-400 mt-0.5">{{ call.did }}</span>
          </div>
        </div>

        <div class="p-3 bg-gray-50 dark:bg-slate-800 border-t border-gray-100 dark:border-slate-800">
          <button class="w-full text-center text-primary font-medium hover:text-blue-700 py-2 text-sm transition-colors rounded-md" @click="goToCalls('all')">
            View All Calls
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useNavbarInfo } from "~/stores/useNabvarInfo";
import { useUserStore } from "~/stores/userStore";
import { useContactStore } from "~/stores/contactStore";
import { watchCallRecords } from "~/services/calls";
import { 
  type CallRecord, 
  isCallAnswered, 
  isCallMissed, 
  isCallInbound, 
  isCallOutbound,
  getCallOutcome
} from "~/utils/calls";
import { sendEmailVerification, type Auth } from "firebase/auth";
import { getEmailVerificationState } from "~/services/user";

const navbar = useNavbarInfo();
navbar.changeInfo("Dashboard", []);

useHead({
  title: "Dashboard",
});

const router = useRouter();
const userStore = useUserStore();
const contactStore = useContactStore();
const { $auth, $rtdb } = useNuxtApp();

const userId = computed(() => userStore.userDetails?.userId);

const allRecords = ref<CallRecord[]>([]);
const isLoading = ref(true);

const isVerifyingEmail = ref(false);
const emailVerificationState = computed(() => getEmailVerificationState(userStore.userDetails));

const verifyEmail = async () => {
  const auth = $auth as Auth;
  if (!auth.currentUser) return;
  isVerifyingEmail.value = true;
  try {
    await sendEmailVerification(auth.currentUser, {
      url: window.location.origin + "/auth/verify-email",
    });
    alert("Verification email sent. Please check your inbox.");
  } catch (error) {
    const errorMsg = typeof error === "object" && error !== null && "code" in error
        ? String(error.code || "Failed to send verification email.")
        : "Failed to send verification email.";
    alert(errorMsg);
  } finally {
    isVerifyingEmail.value = false;
  }
};

const firstName = computed(() => {
  return userStore.userDetails?.firstName || "";
});

// Tracks client-side hydration — prevents auth-dependent content from
// causing server/client VNode mismatches during hydration.
const hasHydrated = ref(false);

// Computed on the client only to avoid SSR/client time-zone hydration mismatches.
const greeting = ref('');
onMounted(() => {
  hasHydrated.value = true;

  const hour = new Date().getHours();
  if (hour < 12) greeting.value = 'Good morning';
  else if (hour < 17) greeting.value = 'Good afternoon';
  else greeting.value = 'Good evening';

  // Initialize contact store for name resolution
  const currentUser = ($auth as any)?.currentUser;
  if (currentUser) {
    contactStore.initialize(currentUser.uid, $rtdb as any).catch(console.error);
  }
});

const isSameDay = (date1: Date, date2: Date) => {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

const answeredCount = computed(() => {
  const now = new Date();
  return allRecords.value.filter((call: CallRecord) => {
    const callDate = new Date(call.startEpoch * 1000);
    return isCallAnswered(call) && isCallInbound(call) && isSameDay(callDate, now);
  }).length;
});

const missedCount = computed(() => {
  const now = new Date();
  return allRecords.value.filter((call: CallRecord) => {
    const callDate = new Date(call.startEpoch * 1000);
    return isCallMissed(call) && isSameDay(callDate, now);
  }).length;
});

const recentCalls = computed(() => {
  return allRecords.value.slice(0, 5).map((r: CallRecord) => ({
    ...r,
    durationSeconds: r.billSec || r.talkTimeSec || 0
  }));
});

const getActualNumber = (call: CallRecord) => {
  if (isCallInbound(call)) {
    return call.callerId || call.callerNumber || 'Unknown';
  }
  return call.destinationNumber || 'Unknown';
};

const getMatchedName = (call: CallRecord) => {
  const number = getActualNumber(call);
  if (number === 'Unknown') return null;
  return contactStore.getNameForNumber(number);
};

const getDisplayTitle = (call: CallRecord) => {
  return getMatchedName(call) || getActualNumber(call);
};

const formatTimeInfo = (call: CallRecord) => {
  const date = new Date(call.startEpoch * 1000);
  
  const today = new Date();
  const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isSameDay(date, today)) {
    return timeString;
  } else if (isSameDay(date, new Date(today.getTime() - 86400000))) {
    return `Yesterday, ${timeString}`;
  }
  return `${date.toLocaleDateString()}, ${timeString}`;
};

const formatDuration = (seconds: number) => {
  const dur = seconds ?? 0;
  if (dur === 0) return '0s';
  const m = Math.floor(dur / 60);
  const s = dur % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const goToCalls = (filter?: string) => {
  if (filter) {
    router.push({ path: "/calls", query: { filter } });
  } else {
    router.push("/calls");
  }
};

const goToSupport = () => {
  router.push("/support");
};

let unsubscribe = () => {};

watch(userId, (newId) => {
  if (newId) {
    isLoading.value = true;
    unsubscribe();
    unsubscribe = watchCallRecords(newId, (records: CallRecord[]) => {
      allRecords.value = records || [];
      isLoading.value = false;
    });
  }
}, { immediate: true });

onUnmounted(() => {
  unsubscribe();
});
</script>
